import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@platform/i18n';
import { CameraStage } from '../components/CameraStage';
import { ReviewStage } from '../components/ReviewStage';
import { useCamera } from '../hooks/useCamera';
import { useFaceGuidance } from '../hooks/useFaceGuidance';
import { parseCaptureMode } from '../lib/captureMode';
import { captureVideoFrame } from '../lib/downscale';
import { postCaptureError, postCaptureSuccess } from '../lib/messaging';
import type { CameraErrorKind, CaptureStage } from '../types';

/** Seconds the face must stay framed before auto-capture fires. */
const AUTO_CAPTURE_COUNTDOWN_S = 2;

/**
 * The page ESS embeds in its clock dialog (~600×600 iframe). This page owns
 * the entire camera UX; ESS owns every face-attendance API call. It sends
 * exactly one postMessage: success (after the user confirms the shot) or
 * error (unrecoverable camera failure).
 *
 * Capture is automatic, mobile-app parity: a stably framed face arms a short
 * countdown and the shutter fires by itself, landing on the review step —
 * the user still confirms before anything is posted to ESS (the spec forbids
 * auto-submit of un-reviewed captures, not auto-capture).
 */
export function FaceCapturePage() {
  const { t } = useT('face-capture');
  const mode = useMemo(() => parseCaptureMode(window.location.search), []);
  // Standalone (not in an iframe): there is no ESS parent to close the dialog
  // after the success message, so the done screen offers a Retry for testing.
  // Embedded, the screen stays passive exactly per the integration spec.
  const standalone = useMemo(() => window.parent === window, []);

  const [stage, setStage] = useState<CaptureStage>('camera');
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  const cameraOn = stage === 'camera';
  const { videoRef, status: cameraStatus, errorKind } = useCamera(cameraOn);
  const { guidance, shutterReady, faces, frameSize, eyeOpenness, manualFallback } =
    useFaceGuidance(videoRef, cameraOn && cameraStatus === 'active');

  // Unrecoverable camera failure → tell ESS once, then show the local error pane.
  const errorPostedRef = useRef(false);
  useEffect(() => {
    if (cameraStatus !== 'error' || errorPostedRef.current) return;
    errorPostedRef.current = true;
    postCaptureError(cameraErrorMessage(errorKind));
    setStage('error');
  }, [cameraStatus, errorKind]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    try {
      setImageBase64(captureVideoFrame(video));
      setStage('review');
    } catch {
      // Frame not ready yet — keep previewing; the shutter stays available.
    }
  }, [videoRef]);

  // Auto-capture: armed only while the ML detector reports a stable,
  // well-framed face ('ready' + shutterReady). Losing the framing disarms the
  // countdown via cleanup. Never arms in detector-unavailable mode — that
  // path stays manual so we can't auto-fire on an unverified frame.
  const autoArmed =
    cameraOn && cameraStatus === 'active' && shutterReady && guidance === 'ready';
  useEffect(() => {
    if (!autoArmed) return;
    setCountdown(AUTO_CAPTURE_COUNTDOWN_S);
    const ticks: ReturnType<typeof setTimeout>[] = [];
    for (let s = 1; s < AUTO_CAPTURE_COUNTDOWN_S; s++) {
      ticks.push(
        setTimeout(() => setCountdown(AUTO_CAPTURE_COUNTDOWN_S - s), s * 1000),
      );
    }
    ticks.push(setTimeout(handleCapture, AUTO_CAPTURE_COUNTDOWN_S * 1000));
    return () => {
      ticks.forEach(clearTimeout);
      setCountdown(null);
    };
  }, [autoArmed, handleCapture]);

  const handleRetake = useCallback(() => {
    setImageBase64(null);
    setStage('camera');
  }, []);

  const handleConfirm = useCallback(() => {
    if (!imageBase64) return;
    postCaptureSuccess(imageBase64);
    setStage('done');
  }, [imageBase64]);

  return (
    <main className="relative mx-auto h-dvh max-w-[600px] overflow-hidden bg-black">
      {stage === 'camera' && (
        <CameraStage
          videoRef={videoRef}
          cameraActive={cameraStatus === 'active'}
          guidance={guidance}
          shutterReady={shutterReady}
          countdown={countdown}
          faces={faces}
          frameSize={frameSize}
          eyeOpenness={eyeOpenness}
          manualFallback={manualFallback}
          onCapture={handleCapture}
        />
      )}

      {stage === 'review' && imageBase64 && (
        <ReviewStage
          imageBase64={imageBase64}
          onRetake={handleRetake}
          onConfirm={handleConfirm}
        />
      )}

      {stage === 'done' && (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
          <p role="status" className="text-base text-white">
            {t(`done.${mode}`)}
          </p>
          {standalone && (
            <button
              type="button"
              onClick={handleRetake}
              className="rounded-full border border-white/60 px-5 py-2 text-base font-semibold text-white transition hover:bg-white/10"
            >
              {t('buttons.retry')}
            </button>
          )}
        </div>
      )}

      {stage === 'error' && (
        <p
          role="alert"
          className="flex h-full items-center justify-center px-6 text-center text-base text-white"
        >
          {t(`error.${errorKind ?? 'generic'}`)}
        </p>
      )}
    </main>
  );
}

/**
 * Messages sent to ESS are shown to the user verbatim in an ESS notification,
 * so they must be human-readable. ESS passes no locale, so these stay English.
 */
function cameraErrorMessage(kind: CameraErrorKind | null): string {
  switch (kind) {
    case 'denied':
      return 'Camera access was denied. Please allow camera access and try again.';
    case 'unavailable':
      return 'No usable camera was found on this device.';
    default:
      return 'The camera could not be started. Please try again.';
  }
}
